import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { ProductSize, ProductTypeOption } from "../models/ProductSize";

const roundFloat = (value: number | null, decimals = 3): number | null =>
	value != null ? parseFloat(value.toFixed(decimals)) : null;

const sanitizeFloats = (row: ProductSize): ProductSize => ({
	...row,
	Weight: roundFloat(row.Weight),
	FinishSpeedGoal: roundFloat(row.FinishSpeedGoal),
	Stand1GapGoal: roundFloat(row.Stand1GapGoal),
	Stand17GapGoal: roundFloat(row.Stand17GapGoal),
	TPHGoal: roundFloat(row.TPHGoal),
	Dim1: roundFloat(row.Dim1),
	Dim2: roundFloat(row.Dim2),
	Dim3: roundFloat(row.Dim3),
});

export const getProductSizes = async ({
	id = null,
}: {
	id?: number | null;
} = {}): Promise<ProductSize[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: false }];

	if (id) {
		parameters.push({ name: "Id", value: id });
	}

	const resp = await apiService.callV2("[MILL].[GetProductSizes]", parameters);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as ProductSize[];
		return rows.map(sanitizeFloats);
	}
	throw new Error(resp.message || "Error fetching products");
};

export const upsertProductSize = async (
	product: ProductSize,
): Promise<ProductSize> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: product.Id },
		{ name: "Name", value: product.Name || "-" },
		{ name: "ProductTypeId", value: product.ProductTypeId ?? -1 },
		{ name: "SectionCode", value: product.SectionCode || "-" },
		{ name: "Description", value: product.Description || "-" },
		{
			name: "SpecialMillComments",
			value: product.SpecialMillComments || "-",
		},
		{ name: "Weight", value: product.Weight ?? -1 },
		{ name: "Active", value: product.Active },
		{ name: "FinishSpeedGoal", value: product.FinishSpeedGoal ?? -1 },
		{ name: "Stand1GapGoal", value: product.Stand1GapGoal ?? -1 },
		{ name: "Stand17GapGoal", value: product.Stand17GapGoal ?? -1 },
		{ name: "TPHGoal", value: product.TPHGoal ?? -1 },
		{ name: "Dim1", value: product.Dim1 ?? -1 },
		{ name: "Dim2", value: product.Dim2 ?? -1 },
		{ name: "Dim3", value: product.Dim3 ?? -1 },
	];

	const resp = await apiService.callV2(
		"[MILL].[UpsertProductSize]",
		parameters,
	);

	if (resp.ok) {
		const row = get(resp, "data.tables[0].rows[0]", null) as ProductSize;
		return row ? sanitizeFloats(row) : row;
	}
	throw new Error(resp.message || "Error saving product");
};

export const duplicateProductSize = async (input: {
	id: number;
	name: string;
	sectionCode: string;
}): Promise<ProductSize> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: input.id },
		{ name: "Name", value: input.name },
		{ name: "SectionCode", value: input.sectionCode },
	];

	const resp = await apiService.callV2(
		"[MILL].[DuplicateProductSize]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error duplicating product");
	}

	const newId = get(resp, "data.tables[0].rows[0].Id", 0) as number;
	const [created] = await getProductSizes({ id: newId });
	return created;
};

export const removeProductSize = async (id: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: id }];

	const resp = await apiService.callV2(
		"[MILL].[RemoveProductSize]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting product");
	}
};

export const getActiveProductTypes = async (): Promise<ProductTypeOption[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2("[MILL].[GetProductTypes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as ProductTypeOption[];
	}
	throw new Error(resp.message || "Error fetching product types");
};
